import React from 'react';

import { Button, Group, List, Modal, Skeleton, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert/KiloAlert';
import useDeleteClassType from 'hooks/classType/useDeleteClassType';
import useAffectedPackagesForEventType from 'hooks/package/useAffectedPackagesForEventType';
import { KiloClassType } from 'types';
import notificationHandler from 'utils/notifications';

const SPEC_COPY =
  'Deleting this class type will remove it from any package attendance rules that reference it. ' +
  "This action can't be undone.";

const ModalClassTypeDelete = ({
  opened,
  kiloClassType,
  handleClose,
}: {
  opened: boolean;
  handleClose: () => void;
  kiloClassType: KiloClassType;
}): JSX.Element => {
  const { mutate, isLoading } = useDeleteClassType();
  const { successNotification, errorNotification } = notificationHandler();

  const { data: affectedResponse, isLoading: isLoadingAffected } = useAffectedPackagesForEventType(
    opened ? kiloClassType.kiloClassTypeId : undefined,
  );
  const affectedPackages = affectedResponse?.data.results ?? [];

  const handleDelete = (): void => {
    mutate(kiloClassType.kiloClassTypeId, {
      onSuccess: () => {
        successNotification('Class type delete success', `${kiloClassType.name} successfully deleted.`);
      },
      onError: error => {
        errorNotification('Class type delete failed', error.response?.data.message);
      },
      onSettled: () => {
        handleClose();
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Delete Class Type</Title>}
      size='auto'
    >
      <Stack>
        <Text>{`Are you sure you want to delete ${kiloClassType.name}?`}</Text>
        <Text color='dimmed'>{SPEC_COPY}</Text>
        {isLoadingAffected && <Skeleton height={60} />}
        {!isLoadingAffected && affectedPackages.length > 0 && (
          <KiloAlert variantStyle='containedWarning' title='Packages that reference this class type' m={0}>
            <List size='sm'>
              {affectedPackages.map(p => (
                <List.Item key={p.packageId}>{p.name}</List.Item>
              ))}
            </List>
          </KiloAlert>
        )}
        <Group position='right'>
          <Button onClick={handleDelete} color='red' loading={isLoading}>
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalClassTypeDelete;
